<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Modal from "@/ui/components/modal.vue";
import { useI18n } from "vue-i18n";
import {
	required,
	minLength,
	titleInputRules,
} from "@/ui/validation/validationRules";

// Types
import ABTestDTO = App.Data.ABTest.ABTestDTO;
import ABTestPostDTO = App.Data.ABTest.ABTestPostDTO;
import ABTestPatchDTO = App.Data.ABTest.ABTestPatchDTO;

const props = defineProps({
	mode: {
		type: String as () => "create" | "edit",
		default: "create",
	},
});

const visible = defineModel<boolean>("visible", { default: false });
const model = defineModel<ABTestDTO | null>("model", { default: null });

const { t } = useI18n();
const emit = defineEmits<{
	create: [test: ABTestPostDTO];
	update: [test: ABTestPatchDTO];
}>();

// Form state
const formData = ref({
	name: "",
	description: "",
	controlVariantName: "Control",
});

const isFormValid = ref(false);

// Computed properties
const modalTitle = computed(() =>
	props.mode === "create" ? "Create New AB Test" : "Edit AB Test"
);

const actionButtonText = computed(() =>
	props.mode === "create" ? "Create Test" : "Save Changes"
);

// Reset form when modal opens/closes or mode changes
watch(
	[visible, () => props.mode, model],
	() => {
		if (visible.value) {
			if (props.mode === "edit" && model.value) {
				// Edit mode - populate form with existing data
				formData.value = {
					name: model.value.name,
					description: model.value.description || "",
					controlVariantName:
						model.value.variants.find((v) => v.is_control)?.name || "Control",
				};
			} else {
				// Create mode - reset form to defaults
				formData.value = {
					name: "",
					description: "",
					controlVariantName: "Control",
				};
			}
		}
	},
	{ immediate: true }
);

// Form submission
function handleSubmit() {
	if (!isFormValid.value) return;

	if (props.mode === "create") {
		// Create new test with initial control variant
		const newTest: ABTestPostDTO = {
			name: formData.value.name,
			description: formData.value.description,
			variants: [
				{
					name: formData.value.controlVariantName,
					percentage: 100,
					is_control: true,
					is_locked: false,
				},
			],
		};
		emit("create", newTest);
	} else if (model.value) {
		// Update existing test
		const updatedTest: ABTestPatchDTO = {
			name: formData.value.name,
			description: formData.value.description,
		};
		emit("update", updatedTest);
	}

	visible.value = false;
}
</script>

<template>
	<Modal
		v-model:visible="visible"
		:title="modalTitle"
		icon="fa-solid fa-flask"
		width="600px"
	>
		<v-form class="ab-test-form" v-model="isFormValid">
			<!-- Test Information Section -->
			<v-text-field
				v-model="formData.name"
				label="Test Name"
				autocomplete="off"
				hide-details="auto"
				:rules="titleInputRules(t)"
				class="mb-4"
			/>

			<v-textarea
				v-model="formData.description"
				label="Description"
				hide-details="auto"
				rows="3"
				placeholder="Optional description of the test"
			/>

			<v-divider class="my-2" v-if="props.mode === 'create'" />

			<!-- Control Variant (only in create mode) -->
			<div v-if="props.mode === 'create'" class="section">
				<h3 class="text-subtitle-1 font-weight-bold">Control Variant</h3>

				<div class="variant-item pa-3 mb-3">
					<v-text-field
						v-model="formData.controlVariantName"
						label="Control Variant Name"
						variant="outlined"
						hide-details="auto"
						density="comfortable"
						:rules="[required(t), minLength(t, 2)]"
						placeholder="Enter name for the control variant"
					/>

					<div class="d-flex align-center mt-2">
						<v-badge color="success" content="Control" inline></v-badge>
						<span class="text-caption ml-auto">100% Traffic</span>
					</div>
				</div>

				<div class="text-caption text-grey">
					You can add more variants after creating the test.
				</div>
			</div>
		</v-form>

		<!-- Modal Actions -->
		<template #actions>
			<v-btn color="error" variant="text" @click="visible = false">
				Cancel
			</v-btn>
			<v-btn
				color="primary"
				variant="elevated"
				:disabled="isFormValid === false"
				@click="handleSubmit"
			>
				{{ actionButtonText }}
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss">
.ab-test-form {
	max-height: 70vh;
	overflow-y: auto;
	padding-right: 8px;

	.variant-item {
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 8px;
		border-left: 4px solid #4caf50;
		background-color: rgba(76, 175, 80, 0.04);
	}
}
</style>
